import { useRef, useState } from 'react';
import { uploadMilestoneFile, deleteMilestoneFile, getMilestoneFileLink } from '../../../api/prospectApi';
import './MilestoneFilePanel.css';

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileIcon(fileName) {
  if (!fileName) return '📄';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.xlsm')) return '📊';
  if (lower.endsWith('.docx') || lower.endsWith('.doc')) return '📝';
  if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) return '📑';
  if (lower.endsWith('.pdf')) return '📕';
  return '📄';
}

export default function MilestoneFilePanel({
  prospectId,
  stageKey,
  milestoneKey,
  files,
  onJourneyUpdated,
  readOnly,
  showProfileDownload,
  clientId,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [expandedVersions, setExpandedVersions] = useState({});
  const [openingFileId, setOpeningFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);

  function handleUploadClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  async function handleFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      const updated = await uploadMilestoneFile(prospectId, stageKey, milestoneKey, file);
      onJourneyUpdated(updated);
    } catch (ex) {
      setUploadError(ex.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleOpenFile(fileId, fileName) {
    setOpeningFileId(fileId);
    try {
      const result = await getMilestoneFileLink(prospectId, stageKey, milestoneKey, fileId);
      const link = result.protocol_link || result.protocolLink;
      if (link) {
        window.open(link, '_self');
      }
    } catch (ex) {
      alert('Could not open file: ' + (ex.message || 'Unknown error'));
    } finally {
      setOpeningFileId(null);
    }
  }

  async function handleDeleteFile(fileId, fileName) {
    if (!window.confirm('Delete "' + fileName + '"? This cannot be undone.')) return;

    setDeletingFileId(fileId);
    try {
      const updated = await deleteMilestoneFile(prospectId, stageKey, milestoneKey, fileId);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Could not delete file: ' + (ex.message || 'Unknown error'));
    } finally {
      setDeletingFileId(null);
    }
  }

  function handleDownloadSnapshot() {
    window.open('/api/clients/' + clientId + '/profile-data/export/excel', '_blank');
  }

  function toggleVersions(fileId) {
    setExpandedVersions(prev => ({ ...prev, [fileId]: !prev[fileId] }));
  }

  const currentFiles = files || [];

  return (
    <div className="mfp__panel">
      <div className="mfp__header">
        <span className="mfp__heading">📎 Working Files</span>
        <div className="mfp__header-actions">
          {showProfileDownload && (
            <button className="mfp__snapshot-btn" onClick={handleDownloadSnapshot} title="Download profile data as Excel">
              ⬇ Profile Snapshot
            </button>
          )}
          {!readOnly && (
            <button
              className="mfp__upload-btn"
              onClick={handleUploadClick}
              disabled={uploading}
              title="Upload a file"
            >
              {uploading ? 'Uploading...' : '+ Upload File'}
            </button>
          )}
        </div>
      </div>

      {uploadError && (
        <div className="mfp__error">{uploadError}</div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="mfp__file-input"
        onChange={handleFileSelected}
        accept="*/*"
      />

      {currentFiles.length === 0 ? (
        <div className="mfp__empty">No files uploaded yet.</div>
      ) : (
        <div className="mfp__file-list">
          {currentFiles.map(function(file) {
            const versionCount = (file.version_history || []).length;
            const isExpanded = !!expandedVersions[file.file_id];

            return (
              <div key={file.file_id} className="mfp__file-entry">
                <div className="mfp__file-row">
                  <span className="mfp__file-icon">{fileIcon(file.file_name)}</span>
                  <div className="mfp__file-info">
                    <span className="mfp__file-name">{file.file_name}</span>
                    <span className="mfp__file-meta">
                      {formatDate(file.uploaded_at)} · {formatFileSize(file.file_size_bytes)}
                    </span>
                  </div>
                  <div className="mfp__file-actions">
                    {versionCount > 0 && (
                      <button
                        className="mfp__versions-btn"
                        onClick={() => toggleVersions(file.file_id)}
                        title="View version history"
                      >
                        {isExpanded ? '▲' : '▶'} {versionCount} version{versionCount > 1 ? 's' : ''}
                      </button>
                    )}
                    <button
                      className="mfp__open-btn"
                      onClick={() => handleOpenFile(file.file_id, file.file_name)}
                      disabled={openingFileId === file.file_id}
                      title="Open in desktop app"
                    >
                      {openingFileId === file.file_id ? '...' : 'Open'}
                    </button>
                    {!readOnly && (
                      <button
                        className="mfp__delete-btn"
                        onClick={() => handleDeleteFile(file.file_id, file.file_name)}
                        disabled={deletingFileId === file.file_id}
                        title="Delete file"
                      >
                        {deletingFileId === file.file_id ? '...' : '✕'}
                      </button>
                    )}
                  </div>
                </div>

                {isExpanded && versionCount > 0 && (
                  <div className="mfp__version-list">
                    {(file.version_history || []).map(function(version, idx) {
                      return (
                        <div key={version.file_id || idx} className="mfp__version-row">
                          <span className="mfp__version-label">v{idx + 1}</span>
                          <span className="mfp__version-date">{formatDate(version.uploaded_at)}</span>
                          <span className="mfp__version-size">{formatFileSize(version.file_size_bytes)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
